import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { OwnerFields, emptyOwner, findOwnerDuplicates, DuplicateOwnerDialog } from "@/components/OwnerFields";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const STATI = ["Attivo", "In pausa", "Concluso", "Da ricontattare"];
const SESSI = ["M", "F", "Non specificato"];
const REPRO = ["Intero/a", "Sterilizzato/a chirurgicamente", "Castrazione chimica", "Non noto"];
const TIPI = ["Cliente", "Cane personale"];

const mapLegacySter = (s) => (s === "Sì" || s === "Si" ? "Sterilizzato/a chirurgicamente" : s === "No" ? "Intero/a" : "Non noto");
const addMonths = (iso, n) => {
  if (!iso || n === "" || n === null || n === undefined) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const months = Number(n);
  if (!m || !Number.isFinite(months)) return "";
  const y = Number(m[1]), mo = Number(m[2]), day = Number(m[3]);
  const total = (y * 12 + (mo - 1)) + months;
  const ty = Math.floor(total / 12);
  const tm = total % 12; // 0-indexed target month
  const lastDay = new Date(ty, tm + 1, 0).getDate();
  const td = Math.min(day, lastDay);
  return `${String(ty).padStart(4, "0")}-${String(tm + 1).padStart(2, "0")}-${String(td).padStart(2, "0")}`;
};

const empty = {
  nome: "", owner_id: "", razza: "", sesso: "Non specificato", data_nascita: "", eta_testo: "",
  sterilizzato: "Non noto", reproductive_status: "", chemical_castration: null,
  microchip: "", peso: "", foto_url: "", tipo_caso: "Cliente",
  percorso: "", problema: "", stato: "Attivo", data_primo_incontro: "", note: "",
  veterinario: "", veterinario_comportamentalista: "", drive_url: "",
  health_alert: "", current_health_conditions: "", medical_history: "", surgeries_injuries: "",
  current_medications: "", allergies_intolerances: "", veterinary_restrictions: "",
};

const Head = ({ children }) => (
  <div className="col-span-2 pt-2 first:pt-0">
    <h3 className="font-head text-xs font-bold uppercase tracking-wide text-muted-foreground">{children}</h3>
  </div>
);

export const DogDialog = ({ open, onOpenChange, dog, fixedOwnerId, onSaved }) => {
  const editing = !!dog;
  const [form, setForm] = useState(empty);
  const [owners, setOwners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creatingOwner, setCreatingOwner] = useState(false);
  const [newOwner, setNewOwner] = useState(emptyOwner);
  const [dupMatches, setDupMatches] = useState([]);

  useEffect(() => {
    if (open) {
      const base = dog ? { ...empty, ...dog } : { ...empty, owner_id: fixedOwnerId || "" };
      if (!base.reproductive_status) base.reproductive_status = mapLegacySter(base.sterilizzato);
      if (!base.chemical_castration) base.chemical_castration = { start_date: "", duration_months: "", end_date: "", notes: "" };
      setForm(base);
      setCreatingOwner(false);
      setNewOwner(emptyOwner);
      setDupMatches([]);
      api.get("/owners").then((r) => setOwners(r.data)).catch(() => {});
    }
  }, [open, dog, fixedOwnerId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const sel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setCC = (k) => (val) => setForm((f) => {
    const cc = { ...(f.chemical_castration || {}), [k]: val };
    if (k === "start_date" || k === "duration_months") cc.end_date = addMonths(cc.start_date, cc.duration_months);
    return { ...f, chemical_castration: cc };
  });

  const saveDog = async (ownerId) => {
    setSaving(true);
    try {
      // Keep legacy 'sterilizzato' in sync for backward compatibility.
      const legacy = form.reproductive_status === "Sterilizzato/a chirurgicamente" ? "Sì"
        : form.reproductive_status === "Intero/a" ? "No" : "Non noto";
      const cc = form.reproductive_status === "Castrazione chimica"
        ? { ...form.chemical_castration, duration_months: form.chemical_castration?.duration_months ? Number(form.chemical_castration.duration_months) : null }
        : null;
      const payload = { ...form, owner_id: ownerId, sterilizzato: legacy, chemical_castration: cc };
      const res = editing ? await api.put(`/dogs/${dog.id}`, payload) : await api.post("/dogs", payload);
      toast.success(editing ? "Cane aggiornato" : "Cane creato");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (force = false) => {
    if (!form.nome.trim()) return toast.error("Il nome del cane è obbligatorio.");
    if (creatingOwner) {
      if (!newOwner.nome.trim()) return toast.error("Il nome del proprietario è obbligatorio.");
      const dups = findOwnerDuplicates(owners, newOwner);
      if (dups.length && !force) { setDupMatches(dups); return; }
      setSaving(true);
      try {
        const or = await api.post("/owners", newOwner);
        setSaving(false);
        await saveDog(or.data.id);
      } catch (e) {
        toast.error(apiErr(e));
        setSaving(false);
      }
      return;
    }
    if (!form.owner_id) return toast.error("Seleziona un proprietario.");
    await saveDog(form.owner_id);
  };

  const ownerValue = creatingOwner ? "__new__" : (form.owner_id || "");
  const onOwnerChange = (v) => {
    if (v === "__new__") setCreatingOwner(true);
    else { setCreatingOwner(false); setForm((f) => ({ ...f, owner_id: v })); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto wg-scroll">
        <DialogHeader>
          <DialogTitle className="font-head">{editing ? "Modifica cane" : "Nuovo cane"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1 col-span-2">
            <Label>Proprietario *</Label>
            <Select value={ownerValue} onValueChange={onOwnerChange} disabled={!!fixedOwnerId && !editing}>
              <SelectTrigger data-testid="dog-owner"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>)}
                {!editing && !fixedOwnerId && <SelectItem value="__new__" data-testid="dog-owner-new-opt">+ Crea nuovo proprietario</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {creatingOwner && (
            <div className="col-span-2 grid grid-cols-2 gap-3 rounded-xl border border-border p-3 bg-secondary/40" data-testid="dog-inline-owner">
              <Head>Nuovo proprietario</Head>
              <OwnerFields form={newOwner} setForm={setNewOwner} tid="dog-newowner" showStato={false} />
            </div>
          )}

          <Head>Dati del cane</Head>
          <div className="space-y-1"><Label>Nome *</Label><Input data-testid="dog-nome" value={form.nome} onChange={set("nome")} /></div>
          <div className="space-y-1"><Label>Razza / incrocio</Label><Input data-testid="dog-razza" value={form.razza} onChange={set("razza")} /></div>
          <div className="space-y-1"><Label>Sesso</Label>
            <Select value={form.sesso} onValueChange={sel("sesso")}>
              <SelectTrigger data-testid="dog-sesso"><SelectValue /></SelectTrigger>
              <SelectContent>{SESSI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Tipo caso</Label>
            <Select value={form.tipo_caso} onValueChange={sel("tipo_caso")}>
              <SelectTrigger data-testid="dog-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Data di nascita</Label><Input type="date" data-testid="dog-nascita" value={form.data_nascita} onChange={set("data_nascita")} /></div>
          <div className="space-y-1"><Label>Età (se nascita ignota)</Label><Input data-testid="dog-eta" value={form.eta_testo} onChange={set("eta_testo")} placeholder="es. ~3 anni" /></div>
          <div className="space-y-1 col-span-2"><Label>Stato riproduttivo</Label>
            <Select value={form.reproductive_status || "Non noto"} onValueChange={sel("reproductive_status")}>
              <SelectTrigger data-testid="dog-reproductive"><SelectValue /></SelectTrigger>
              <SelectContent>{REPRO.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.reproductive_status === "Castrazione chimica" && (
            <div className="col-span-2 grid grid-cols-2 gap-3 rounded-xl border border-border p-3 bg-secondary/40" data-testid="dog-chem-castration">
              <div className="space-y-1"><Label>Data applicazione</Label>
                <Input type="date" data-testid="dog-chem-start" value={form.chemical_castration?.start_date || ""} onChange={(e) => setCC("start_date")(e.target.value)} /></div>
              <div className="space-y-1"><Label>Durata (mesi)</Label>
                <Input type="number" min="1" data-testid="dog-chem-months" value={form.chemical_castration?.duration_months || ""} onChange={(e) => setCC("duration_months")(e.target.value)} /></div>
              <div className="space-y-1"><Label>Fine prevista</Label>
                <Input type="date" data-testid="dog-chem-end" value={form.chemical_castration?.end_date || ""} readOnly className="bg-muted/50" /></div>
              <div className="space-y-1 col-span-2 sm:col-span-1"><Label>Note trattamento</Label>
                <Input data-testid="dog-chem-notes" value={form.chemical_castration?.notes || ""} onChange={(e) => setCC("notes")(e.target.value)} /></div>
            </div>
          )}
          <div className="space-y-1"><Label>Peso (kg)</Label><Input data-testid="dog-peso" value={form.peso} onChange={set("peso")} /></div>
          <div className="space-y-1"><Label>Microchip</Label><Input data-testid="dog-microchip" value={form.microchip} onChange={set("microchip")} /></div>
          <div className="space-y-1"><Label>Foto (URL)</Label><Input data-testid="dog-foto" value={form.foto_url} onChange={set("foto_url")} placeholder="https://…" /></div>

          <Head>Percorso</Head>
          <div className="space-y-1"><Label>Percorso / servizio principale</Label><Input data-testid="dog-percorso" value={form.percorso} onChange={set("percorso")} /></div>
          <div className="space-y-1"><Label>Stato percorso</Label>
            <Select value={form.stato} onValueChange={sel("stato")}>
              <SelectTrigger data-testid="dog-stato"><SelectValue /></SelectTrigger>
              <SelectContent>{STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1 col-span-2"><Label>Data primo incontro</Label><Input type="date" data-testid="dog-primo" value={form.data_primo_incontro} onChange={set("data_primo_incontro")} /></div>
          <div className="space-y-1 col-span-2"><Label>Problema principale</Label><Input data-testid="dog-problema" value={form.problema} onChange={set("problema")} /></div>
          <div className="space-y-1 col-span-2"><Label>Note generali</Label><Textarea data-testid="dog-note" value={form.note} onChange={set("note")} /></div>

          <Head>Salute e informazioni veterinarie</Head>
          <div className="space-y-1 col-span-2"><Label>Informazione sanitaria importante</Label><Textarea data-testid="dog-health-alert" value={form.health_alert} onChange={set("health_alert")} placeholder="Es. allergia grave, condizione da segnalare…" /></div>
          <div className="space-y-1 col-span-2"><Label>Patologie / condizioni attuali</Label><Textarea data-testid="dog-health-conditions" value={form.current_health_conditions} onChange={set("current_health_conditions")} /></div>
          <div className="space-y-1 col-span-2"><Label>Storia sanitaria rilevante</Label><Textarea data-testid="dog-health-history" value={form.medical_history} onChange={set("medical_history")} /></div>
          <div className="space-y-1 col-span-2"><Label>Interventi / traumi importanti</Label><Textarea data-testid="dog-health-surgeries" value={form.surgeries_injuries} onChange={set("surgeries_injuries")} /></div>
          <div className="space-y-1 col-span-2"><Label>Farmaci / terapie in corso</Label><Textarea data-testid="dog-health-medications" value={form.current_medications} onChange={set("current_medications")} /></div>
          <div className="space-y-1 col-span-2"><Label>Allergie / intolleranze</Label><Textarea data-testid="dog-health-allergies" value={form.allergies_intolerances} onChange={set("allergies_intolerances")} /></div>
          <div className="space-y-1 col-span-2"><Label>Indicazioni / limitazioni veterinarie</Label><Textarea data-testid="dog-health-restrictions" value={form.veterinary_restrictions} onChange={set("veterinary_restrictions")} /></div>

          <Head>Riferimenti / note</Head>
          <div className="space-y-1 col-span-2 sm:col-span-1"><Label>Veterinario</Label><Input data-testid="dog-vet" value={form.veterinario} onChange={set("veterinario")} /></div>
          <div className="space-y-1 col-span-2 sm:col-span-1"><Label>Vet. comportamentalista</Label><Input data-testid="dog-vetc" value={form.veterinario_comportamentalista} onChange={set("veterinario_comportamentalista")} /></div>
          <div className="space-y-1 col-span-2"><Label>Cartella Drive (URL)</Label><Input data-testid="dog-drive" value={form.drive_url} onChange={set("drive_url")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="dog-save" onClick={() => submit()} disabled={saving}>{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
      <DuplicateOwnerDialog
        matches={dupMatches}
        onUse={(m) => { setDupMatches([]); setCreatingOwner(false); setForm((f) => ({ ...f, owner_id: m.id })); saveDog(m.id); }}
        onCreateAnyway={() => { setDupMatches([]); submit(true); }}
        onCancel={() => setDupMatches([])}
      />
    </Dialog>
  );
};
